<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-lg-10 col-md-12">
        <div class="card">
          <div class="card-body p-4">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading details...</p>
            </div>

            <template v-else>
            <!-- Assignment Details Box -->
            <div class="border border-dark p-4 mb-4 text-sm rounded" v-if="displayAssignmentDetails">
              <div class="mb-2">
                <strong>LOCATION:</strong> {{ displayAssignmentDetails.location }}
              </div>
              <div class="mb-2">
                <strong>YOUR EMAIL:</strong> {{ displayAssignmentDetails.email }}
              </div>
              <div class="mb-2">
                <strong>ASSIGNMENT GROUP:</strong> {{ displayAssignmentDetails.assignmentGroup }}
              </div>
              <div class="mb-2">
                <strong>ASSIGNMENT GROUP ID:</strong> {{ displayAssignmentDetails.assignmentGroupId }}
              </div>
              <div class="mb-0">
                <strong>ASSIGNMENT DATE:</strong> {{ displayAssignmentDetails.assignmentDate }}
              </div>
            </div>

            <!-- User Message -->
            <div class="mb-4">
              <p class="mb-2">Hello <strong>{{ displayUserName }}</strong>,</p>
              <p class="mb-2 text-success fw-bold">{{ displayMessage }}</p>
              <p class="mb-2" v-if="displayAdditionalMessage">{{ displayAdditionalMessage }}</p>
              <p class="mb-0">Thank you.</p>
            </div>


            <!-- Divider -->
            <hr class="my-4" />

            <!-- Did You Know Section -->
            <div class="mb-4" v-if="showDidYouKnow">
              <h5 class="fw-bold mb-3">Did You Know?</h5>
              <p class="mb-3">{{ displayDidYouKnowText }}</p>
              <ul class="mb-3" v-if="displayDidYouKnowSteps && displayDidYouKnowSteps.length > 0">
                <li v-for="(step, index) in displayDidYouKnowSteps" :key="index" class="mb-2">{{ step }}</li>
              </ul>
              <p class="mb-0" v-if="displayNote">{{ displayNote }}</p>
            </div>

            <!-- Login Link -->
            <router-link to="/sign-in">
              <MaterialButton color="success">
                Click here to Login
              </MaterialButton>
            </router-link>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import MaterialButton from '@/components/common/MaterialButton.vue'
import api from '@/config/api'

const route = useRoute()
const toast = useToast()

// State
const loading = ref(true)
const assignmentDetails = ref({
  location: '',
  email: '',
  assignmentGroup: '',
  assignmentGroupId: '',
  assignmentDate: ''
})
const userName = ref('')
const message = ref('')
const additionalMessage = ref('')
const showDidYouKnow = ref(true)
const didYouKnowText = ref('')
const didYouKnowSteps = ref([])
const note = ref('')

// Mock fallback data
const mockAssignmentDetails = {
  location: 'CS Test Church -> Chicago main Branch',
  email: 'edward.aifah@webitools.com',
  assignmentGroup: 'custodian @ Sunday Morning Service -main',
  assignmentGroupId: '39703',
  assignmentDate: 'Jan 18, 2026 10:00:00 AM'
}
const mockUserName = 'Edward'
const mockMessage = 'You have successfully signed up for this assignment.'
const mockAdditionalMessage = 'The current assignment status is accepted'
const mockDidYouKnowText = 'If your organization has multiple Activities/services, you can choose which activities and/or workgroups you prefer to be scheduled for. Follow the steps below.'
const mockDidYouKnowSteps = [
  "log into the application and click on the 'my Profile/Preferences' button from the Home page",
  "click on the 'Preferred Activities and Workgroups' tab.",
  "select the Activities and/or Workgroups you want to be considered for (you will ONLY be considered for the selected choices).",
  "NOTE: if no selections are made, you will be considered for ALL available selections."
]
const mockNote = "*only relevant when the auto-scheduling engine is enabled"

// Computed display values (prefer API, fallback to mock)
const displayAssignmentDetails = computed(() =>
  assignmentDetails.value && Object.values(assignmentDetails.value).some(v => v)
    ? assignmentDetails.value
    : mockAssignmentDetails
)
const displayUserName = computed(() => userName.value || mockUserName)
const displayMessage = computed(() => message.value || mockMessage)
const displayAdditionalMessage = computed(() => additionalMessage.value || mockAdditionalMessage)
const displayDidYouKnowText = computed(() => didYouKnowText.value || mockDidYouKnowText)
const displayDidYouKnowSteps = computed(() =>
  (didYouKnowSteps.value && didYouKnowSteps.value.length > 0) ? didYouKnowSteps.value : mockDidYouKnowSteps
)
const displayNote = computed(() => note.value || mockNote)

// Process signup confirmation (auto on mount)
const processSignupConfirmation = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) return

  try {
    const response = await api.get(`/account/volunteersignup/cs/${encodedString}`)
    if (response.data?.message) {
      toast.info(response.data.message, { position: 'top-right' })
    }
  } catch (error) {
    console.error('Error processing volunteer signup:', error)
    const errMsg = error.response?.data?.message || error.message || 'Failed to process signup.'
    toast.error(errMsg, { position: 'top-right' })
  }
}

// Fetch page details
const fetchSignupDetails = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) {
    loading.value = false
    return
  }

  try {
    loading.value = true
    const response = await api.get(`/account/volunteersignup-details/cs/${encodedString}`)

    if (response.data) {
      const data = response.data

      if (data.assignmentDetails) {
        assignmentDetails.value = {
          location: data.assignmentDetails.location || '',
          email: data.assignmentDetails.email || '',
          assignmentGroup: data.assignmentDetails.assignment_group || '',
          assignmentGroupId: data.assignmentDetails.assignment_group_id || '',
          assignmentDate: data.assignmentDetails.assignment_date || ''
        }
      }

      userName.value = data.username || ''
      message.value = data.message || ''
      additionalMessage.value = data.additionalMessage || ''
      if (data.showDidYouKnow != null) showDidYouKnow.value = data.showDidYouKnow
      if (data.didYouKnowText != null) didYouKnowText.value = data.didYouKnowText
      if (data.didYouKnowSteps && Array.isArray(data.didYouKnowSteps)) didYouKnowSteps.value = data.didYouKnowSteps
      if (data.note != null) note.value = data.note
    }
  } catch (error) {
    console.error('Error fetching signup confirmation details:', error)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await processSignupConfirmation()
  await fetchSignupDetails()
})
</script>

